import React, { useState, useEffect } from 'react';
import {
  CalendarDaysIcon,
  UsersIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalClients: 0,
    totalCoaches: 0,
    totalBookings: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentSessions, setRecentSessions] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0], // Today
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  // Fetch dashboard data
  const fetchDashboardData = async (start = null, end = null) => {
    try {
      setLoading(true);
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;

      const analyticsData = await adminAPI.getAnalytics(params);

        setStats({
          totalSessions: analyticsData.totals.sessions,
          totalClients: analyticsData.totals.clients,
          totalCoaches: analyticsData.totals.coaches,
          totalBookings: analyticsData.totals.current_bookings // Use current confirmed bookings
        });

        setRecentSessions(analyticsData.today_sessions || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Erreur lors du chargement des données');

        // Fallback to individual API calls if analytics fails
        try {
          const [sessionsData, clientsData, coachesData, bookingsData] = await Promise.all([
            adminAPI.getSessions(),
            adminAPI.getUsers({ role: 'client' }),
            adminAPI.getCoaches(),
            adminAPI.getBookings()
          ]);

          // Handle different response formats
          const sessions = Array.isArray(sessionsData) ? sessionsData : (sessionsData?.results || []);
          const clients = Array.isArray(clientsData) ? clientsData : (clientsData?.results || []);
          const coaches = Array.isArray(coachesData) ? coachesData : (coachesData?.results || []);
          const bookings = Array.isArray(bookingsData) ? bookingsData : (bookingsData?.results || []);

          setStats({
            totalSessions: sessions.length,
            totalClients: clients.length || clientsData?.count || 0,
            totalCoaches: coaches.length || coachesData?.count || 0,
            totalBookings: bookings.filter(b => b.status === 'confirmed').length
          });

          // Get recent sessions for today
          const today = new Date().toISOString().split('T')[0];
          const todaySessions = sessions.filter(session => session.date === today);

          setRecentSessions(todaySessions.slice(0, 5));
        } catch (fallbackError) {
          console.error('Fallback error:', fallbackError);
        }
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchDashboardData(dateRange.startDate, dateRange.endDate);
  }, []);

  const handleDateRangeChange = (field, value) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);
  };

  const applyDateFilter = () => {
    fetchDashboardData(dateRange.startDate, dateRange.endDate);
  };

  const resetDateFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    const defaultRange = {
      startDate: today,
      endDate: today
    };
    setDateRange(defaultRange);
    fetchDashboardData(defaultRange.startDate, defaultRange.endDate);
  };

  const dashboardStats = [
    {
      name: 'Séances totales',
      value: stats.totalSessions.toString(),
      icon: CalendarDaysIcon,
      color: 'primary'
    },
    {
      name: 'Clients actifs',
      value: stats.totalClients.toString(),
      icon: UsersIcon,
      color: 'accent'
    },
    {
      name: 'Coachs',
      value: stats.totalCoaches.toString(),
      icon: ChartBarIcon,
      color: 'success'
    },
    {
      name: 'Réservations confirmées',
      value: stats.totalBookings.toString(),
      icon: CheckCircleIcon,
      color: 'warning'
    }
  ];





  const getStatColor = (color) => {
    switch (color) {
      case 'primary':
        return 'from-primary-500 to-primary-600';
      case 'accent':
        return 'from-accent-500 to-accent-600';
      case 'success':
        return 'from-success-500 to-success-600';
      case 'warning':
        return 'from-warning-500 to-warning-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
          <p className="mt-1 text-sm text-gray-500">
            Vue d'ensemble de votre studio Nazaï
          </p>
        </div>

        {/* Date Filter */}
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Du:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Au:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={applyDateFilter}
              className="px-4 py-1 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 transition-colors"
            >
              Appliquer
            </button>
            <button
              onClick={resetDateFilter}
              className="px-4 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors"
            >
              Aujourd'hui
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div className="flex items-center">
                <div className={`w-12 h-12 bg-gradient-to-r ${getStatColor(stat.color)} rounded-lg flex items-center justify-center`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <div className="flex items-baseline">
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                    <p className={`ml-2 text-sm font-medium ${
                      stat.changeType === 'increase' ? 'text-success-600' : 'text-error-600'
                    }`}>
                      {stat.change}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Sessions */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Séances d'aujourd'hui</h3>
            <span className="text-sm text-gray-500">{recentSessions.length} séances</span>
          </div>

          <div className="space-y-4">
            {recentSessions.length > 0 ? (
              recentSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <ClockIcon className="h-5 w-5 text-primary-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        {session.start_time.slice(0, 5)} - {session.coach?.user?.full_name || 'Coach non défini'}
                      </p>
                      <p className="text-sm text-gray-500">{session.location?.name || 'Lieu non défini'}</p>
                      <p className="text-xs text-gray-400">
                        {session.current_bookings || 0}/{session.max_capacity || 1} réservations
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    (session.current_bookings || 0) >= (session.max_capacity || 1)
                      ? 'text-red-600 bg-red-100'
                      : (session.current_bookings || 0) > 0
                        ? 'text-yellow-600 bg-yellow-100'
                        : 'text-green-600 bg-green-100'
                  }`}>
                    {(session.current_bookings || 0) >= (session.max_capacity || 1)
                      ? 'Complet'
                      : (session.current_bookings || 0) > 0
                        ? 'Partiellement réservé'
                        : 'Disponible'
                    }
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucune séance programmée aujourd'hui</p>
              </div>
            )}
          </div>
        </div>


      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Actions rapides</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left">
            <CalendarDaysIcon className="h-8 w-8 text-primary-600 mb-2" />
            <h4 className="font-medium text-gray-900">Nouvelle réservation</h4>
            <p className="text-sm text-gray-500">Créer une réservation manuelle</p>
          </button>
          
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left">
            <UsersIcon className="h-8 w-8 text-accent-600 mb-2" />
            <h4 className="font-medium text-gray-900">Ajouter un client</h4>
            <p className="text-sm text-gray-500">Inscrire un nouveau client</p>
          </button>
          
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left">
            <ChartBarIcon className="h-8 w-8 text-success-600 mb-2" />
            <h4 className="font-medium text-gray-900">Voir les rapports</h4>
            <p className="text-sm text-gray-500">Consulter les analyses</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
